import { useFormContext, useController } from 'react-hook-form'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { DSFlex, DSInput, DSSpacer, DSText } from '@zoe/ds-web'
import { FieldError } from '../core/fields/FieldError'

const WEIGHT_OPTIONS = [
  { id: 'kg', label: 'kg' },
  { id: 'st', label: 'st' },
  { id: 'lbs', label: 'lbs' },
]

const SegmentedControl = styled.div`
  display: flex;
  gap: 0;
  border-radius: var(--radius-8, 8px);
  overflow: hidden;
  border: 1px solid var(--colour-border-primary, #e2e2e2);
  width: fit-content;
`

const SegmentButton = styled.button<{ $active: boolean }>`
  padding: 0 var(--grid-24);
  height: 36px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--colour-text-primary, #2d3246)' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--colour-text-primary, #2d3246)')};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:not(:last-child) {
    border-right: 1px solid var(--colour-border-primary, #e2e2e2);
  }
`

type WeightScreenFormBodyProps = {
  defaultWeightUnit: string
  selectedUnit?: string
  onUnitChange?: (unit: string) => void
  label?: string
}

export const WeightScreenFormBody: React.FC<WeightScreenFormBodyProps> = ({
  defaultWeightUnit,
  selectedUnit: controlledUnit,
  onUnitChange,
  label,
}) => {
  const {
    formState: { errors, touchedFields },
    setValue,
    clearErrors,
  } = useFormContext<any>()
  const [internalUnit, setInternalUnit] = useState<string>(defaultWeightUnit === 'stone' ? 'st' : defaultWeightUnit)
  const selectedUnit = controlledUnit ? (controlledUnit === 'stone' ? 'st' : controlledUnit) : internalUnit

  // Map display unit back to form unit (st → stone)
  const formUnit = selectedUnit === 'st' ? 'stone' : selectedUnit

  const clearFieldAndErrors = useCallback(
    async (fieldName: string) => {
      setValue(fieldName, undefined)
      clearErrors(fieldName)
    },
    [setValue, clearErrors],
  )

  const resetForm = useCallback(async () => {
    await clearFieldAndErrors('weight_kg')
    await clearFieldAndErrors('weight_lbs')
    await clearFieldAndErrors('weight_st')
    await clearFieldAndErrors('weight_st_lbs')
  }, [clearFieldAndErrors])

  useEffect(() => {
    if (controlledUnit) {
      const mappedUnit = controlledUnit === 'stone' ? 'st' : controlledUnit
      if (mappedUnit !== internalUnit) {
        resetForm().then(() => {
          setInternalUnit(mappedUnit)
          setValue('weight_unit', controlledUnit === 'st' ? 'stone' : controlledUnit, {
            shouldDirty: true,
            shouldValidate: true,
          })
        })
      }
    }
  }, [controlledUnit, internalUnit, resetForm, setValue])

  let selectedError

  if (formUnit === 'stone') {
    selectedError =
      (touchedFields['weight_st'] && errors['weight_st']?.message) ||
      (touchedFields['weight_st_lbs'] && errors['weight_st_lbs']?.message)
  } else {
    selectedError = touchedFields[`weight_${formUnit}`] && errors[`weight_${formUnit}`]?.message
  }

  const onSelect = (option: string) => {
    const formValue = option === 'st' ? 'stone' : option
    resetForm().then(() => {
      setInternalUnit(option)
      setValue('weight_unit', formValue, { shouldDirty: true, shouldValidate: true })
      onUnitChange?.(formValue)
    })
  }

  const weightKg = useController({ name: 'weight_kg' })
  const weightLbs = useController({ name: 'weight_lbs' })
  const weightSt = useController({ name: 'weight_st' })
  const weightStLbs = useController({ name: 'weight_st_lbs' })

  return (
    <div style={{ width: '100%' }}>
      <SegmentedControl>
        {WEIGHT_OPTIONS.map((opt) => (
          <SegmentButton key={opt.id} $active={selectedUnit === opt.id} onClick={() => onSelect(opt.id)} type="button">
            <DSText variant="fluid-paragraph-200" color={selectedUnit === opt.id ? 'primary-inverse' : 'primary'}>
              {opt.label}
            </DSText>
          </SegmentButton>
        ))}
      </SegmentedControl>
      <DSSpacer size={16} direction="vertical" />
      <div>
        {formUnit === 'kg' && (
          <DSInput
            id="weight-kg"
            label={label ?? 'Weight'}
            name={weightKg.field.name}
            value={weightKg.field.value ?? ''}
            onChange={weightKg.field.onChange}
            onBlur={weightKg.field.onBlur}
            placeholder=""
            suffix="kg"
            type="number"
            testId="weight-kg-input"
          />
        )}
        {formUnit === 'lbs' && (
          <DSInput
            id="weight-lbs"
            label={label ?? 'Weight'}
            name={weightLbs.field.name}
            value={weightLbs.field.value ?? ''}
            onChange={weightLbs.field.onChange}
            onBlur={weightLbs.field.onBlur}
            placeholder=""
            suffix="lbs"
            type="number"
            testId="weight-lbs-input"
          />
        )}
        {formUnit === 'stone' && (
          <DSFlex direction="row">
            <DSInput
              id="weight-st"
              label={label ?? 'Weight'}
              name={weightSt.field.name}
              value={weightSt.field.value ?? ''}
              onChange={weightSt.field.onChange}
              onBlur={weightSt.field.onBlur}
              placeholder=""
              suffix="st"
              type="number"
              testId="weight-st-input"
            />
            <DSSpacer size={12} direction="horizontal" />
            <DSInput
              id="weight-st-lbs"
              label={'\u00A0'}
              name={weightStLbs.field.name}
              value={weightStLbs.field.value ?? ''}
              onChange={weightStLbs.field.onChange}
              onBlur={weightStLbs.field.onBlur}
              placeholder=""
              suffix="lbs"
              type="number"
              testId="weight-st-lbs-input"
            />
          </DSFlex>
        )}
      </div>
      {selectedError && <FieldError>{selectedError}</FieldError>}
    </div>
  )
}
